import React, { useState, useEffect } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
} from "@craft-design-system/core-components-react";
import {
	Calendar,
	Users,
	MessageSquare,
	ToggleLeft,
	Clock,
	Hash,
	Archive,
	Tag,
} from "lucide-react";
import { format } from "date-fns";

// Use the same STATUS_BTT_OPTIONS as VFFDataTable
const STATUS_BTT_OPTIONS = [
	"AE",
	"BANF",
	"deleted",
	"Inhouse",
	"NR VSC",
	"Scania",
	"NR EV",
	"Lost/Missing Part",
	"In Stock",
	"E missing",
	"Exceptional reportable",
];

// Archive options (same as VFFDataTable)
const ARCHIVE_OPTIONS = [
	"Unarchived",
	"To be deleted",
	"Duplicate Entry",
	"No Demand",
	"Hausteile",
	"On Hold",
	"PAT",
	"Others",
];

// Helper function to convert display value to backend value
const getArchiveBackendValue = (displayValue) => {
	return displayValue === "Unarchived" ? "N" : displayValue;
};

const VFFBulkEditModal = ({
	open,
	onClose,
	selectedItems,
	onSave,
	isSaving = false,
	processingProgress = null, // Add processing progress prop
	getUserDisplayName,
}) => {
	// Helper function to safely parse dates
	const safeDateParse = (dateValue) => {
		if (!dateValue) return null;
		try {
			const date = new Date(dateValue);
			return !isNaN(date.getTime()) ? date : null;
		} catch (error) {
			console.warn("Invalid date value:", dateValue);
			return null;
		}
	};

	// State for bulk edit fields
	const [bulkEditData, setBulkEditData] = useState({
		ltb_date: null,
		build_date: null,
		btt_status: [],
		part_responsible: "",
		part_followup_date: null,
		part_comment: "",
		snr_ma_nahme_status: null, // null means no change, true/false for toggle
		archive: "Unarchived", // Archive field
		old_snr: "", // Old Part No
		new_snr: "", // New Part No
		tags: "", // comma-separated tag string
	});
	const [fieldChanges, setFieldChanges] = useState({
		ltb_date: false,
		build_date: false,
		btt_status: false,
		part_responsible: false,
		part_followup_date: false,
		part_comment: false,
		snr_ma_nahme_status: false,
		archive: false,
		old_snr: false,
		new_snr: false,
		tags: false,
	});

	// Reset form when modal opens/closes
	useEffect(() => {
		if (open) {
			setBulkEditData({
				ltb_date: null,
				build_date: null,
				btt_status: [],
				part_responsible: "",
				part_followup_date: null,
				part_comment: "",
				snr_ma_nahme_status: null,
				archive: "Unarchived",
				old_snr: "",
				new_snr: "",
				tags: "",
			});
			setFieldChanges({
				ltb_date: false,
				build_date: false,
				btt_status: false,
				part_responsible: false,
				part_followup_date: false,
				part_comment: false,
				snr_ma_nahme_status: false,
				archive: false,
				old_snr: false,
				new_snr: false,
				tags: false,
			});
		}
	}, [open]);

	// Handle field value changes
	const handleFieldChange = (field, value) => {
		setBulkEditData((prev) => ({
			...prev,
			[field]: value,
		}));
		setFieldChanges((prev) => ({
			...prev,
			[field]: true,
		}));
	};

	// Handle toggle field changes
	const handleToggleChange = (field, enabled) => {
		// Text fields that should default to empty string, not false
		const textFields = [
			"part_responsible",
			"part_comment",
			"old_snr",
			"new_snr",
			"tags",
		];
		if (enabled) {
			setBulkEditData((prev) => ({
				...prev,
				[field]: textFields.includes(field) ? "" : false, // Default to '' for text fields, false for toggles
			}));
		} else {
			setBulkEditData((prev) => ({
				...prev,
				[field]: null, // null means no change
			}));
		}
		setFieldChanges((prev) => ({
			...prev,
			[field]: enabled,
		}));
	};

	// Handle save
	const handleSave = () => {
		// Get current user name
		const currentUser = getUserDisplayName ? getUserDisplayName() : "User";

		// Prepare the changes object with only modified fields
		const changes = {};

		Object.keys(fieldChanges).forEach((field) => {
			if (fieldChanges[field]) {
				if (
					field === "ltb_date" ||
					field === "build_date" ||
					field === "part_followup_date"
				) {
					// Format dates for backend - check if date is valid first or if SET_TO_NULL
					if (bulkEditData[field] === "SET_TO_NULL") {
						changes[field] = null;
					} else if (
						bulkEditData[field] &&
						bulkEditData[field] instanceof Date &&
						!isNaN(bulkEditData[field].getTime())
					) {
						changes[field] = format(bulkEditData[field], "yyyy-MM-dd");
					} else {
						changes[field] = null;
					}
				} else if (field === "btt_status") {
					// Handle BTT status array - convert to comma-separated string if array
					// Map btt_status to status_btt for backend compatibility
					if (Array.isArray(bulkEditData[field])) {
						// If array contains "SET_TO_EMPTY", set to null to clear the BTT status
						if (bulkEditData[field].includes("SET_TO_EMPTY")) {
							changes["status_btt"] = null;
						} else if (bulkEditData[field].length > 0) {
							changes["status_btt"] = bulkEditData[field].join(", ");
						} else {
							changes["status_btt"] = null;
						}
					} else {
						changes["status_btt"] = bulkEditData[field];
					}
				} else if (field === "archive") {
					// Convert display value to backend value
					changes[field] = getArchiveBackendValue(bulkEditData[field]);
				} else if (field === "part_responsible") {
					// Handle responsible field with SET_TO_NULL option
					if (bulkEditData[field] === "SET_TO_NULL") {
						changes[field] = null;
					} else {
						changes[field] = bulkEditData[field];
					}
				} else if (field === "part_comment") {
					// Handle comment field - no SET_TO_NULL option
					changes[field] = bulkEditData[field];
				} else if (field === "tags") {
					// Handle tags field - comma-separated string, SET_TO_NULL clears
					changes[field] =
						bulkEditData[field] === "SET_TO_NULL"
							? null
							: bulkEditData[field].trim() || null;
				} else {
					// Handle other fields normally (snr_ma_nahme_status)
					changes[field] = bulkEditData[field];
				}
			}
		});

		// Add user information for comments if comment field is being updated
		if (fieldChanges.part_comment) {
			changes.user_name = currentUser;
		}

		// Call the save function with selected items and changes
		onSave(selectedItems, changes);
	};

	// Check if any changes were made - requires both field to be enabled AND have a value
	const hasChanges = Object.keys(fieldChanges).some((field) => {
		if (!fieldChanges[field]) return false; // Field not enabled

		const value = bulkEditData[field];
		// Check if field has a meaningful value
		switch (field) {
			case "ltb_date":
			case "build_date":
			case "part_followup_date":
				return (
					value === "SET_TO_NULL" ||
					(value instanceof Date && !isNaN(value.getTime()))
				);
			case "btt_status":
				// For BTT status, any selection (including empty array for clearing) is valid when field is enabled
				return true;
			case "part_responsible":
				return value === "SET_TO_NULL" || (value && value.trim() !== "");
			case "part_comment":
				// For comment field, any change is valid when field is enabled (including empty string)
				return true;
			case "snr_ma_nahme_status":
				// For status toggle, any selection (including null for "None") is valid when field is enabled
				return true;
			case "archive":
				// For archive status, any selection (including "Unarchived") is valid when field is enabled
				return value !== null && value !== undefined && value !== "";
			case "old_snr":
			case "new_snr":
				return value !== null && value !== undefined && value !== "";
			case "tags":
				return value === "SET_TO_NULL" || (value && value.trim() !== "");
			default:
				return value !== null && value !== undefined && value !== "";
		}
	});

	// Date <-> native input helpers
	const dateInputValue = (v) => {
		const d = safeDateParse(v);
		return d ? format(d, "yyyy-MM-dd") : "";
	};
	const dateFromInput = (val) => (val ? new Date(`${val}T00:00:00`) : null);

	// BTT multi-select helpers
	const bttValues = Array.isArray(bulkEditData.btt_status)
		? bulkEditData.btt_status
		: [];
	const bttIsEmptySentinel = bttValues.includes("SET_TO_EMPTY");
	const toggleBttOption = (option) => {
		const base = bttValues.filter((v) => v !== "SET_TO_EMPTY");
		const next = base.includes(option)
			? base.filter((v) => v !== option)
			: [...base, option];
		handleFieldChange("btt_status", next);
	};

	const s = {
		shell: {
			width: "1200px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "74vh",
			position: "relative",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		scrollArea: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			paddingRight: "2px",
		},
		sectionHead: {
			display: "flex",
			alignItems: "center",
			gap: "8px",
			marginBottom: "8px",
			marginTop: "4px",
		},
		sectionTitle: {
			margin: 0,
			fontSize: "15px",
			color: "#1e2832",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		},
		emptyBox: {
			padding: "40px",
			textAlign: "center",
			color: "#999",
			backgroundColor: "#f9f9f9",
			border: "1px dashed #ddd",
			borderRadius: "6px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		tableWrap: {
			maxHeight: "260px",
			overflow: "auto",
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			marginBottom: "20px",
			backgroundColor: "white",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			fontSize: "12px",
		},
		th: {
			textAlign: "left",
			padding: "9px 12px",
			backgroundColor: "#f8f9fa",
			borderBottom: "1px solid #e5e7eb",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			fontSize: "11px",
			color: "#374151",
			position: "sticky",
			top: 0,
			zIndex: 1,
			whiteSpace: "nowrap",
		},
		td: {
			padding: "7px 12px",
			borderBottom: "1px solid #f1f5f9",
			color: "#374151",
			whiteSpace: "nowrap",
		},
		grid: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
			gap: "14px",
			alignItems: "start",
		},
		card: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			overflow: "hidden",
			backgroundColor: "#fbfbfb",
			transition: "border-color 0.2s ease, box-shadow 0.2s ease",
		},
		cardActive: {
			border: "1px solid #1e2832",
			borderRadius: "8px",
			overflow: "hidden",
			backgroundColor: "white",
			boxShadow: "0 4px 14px rgba(30,40,50,0.10)",
		},
		cardHeader: (active) => ({
			display: "flex",
			alignItems: "center",
			gap: "10px",
			padding: "12px 14px",
			cursor: "pointer",
			backgroundColor: active ? "#ffffff" : "#f8f9fa",
			borderBottom: active ? "1px solid #eee" : "1px solid transparent",
		}),
		cardLabel: (active, accent) => ({
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: active ? accent : "#374151",
		}),
		checkbox: {
			transform: "scale(1.2)",
			accentColor: "#cc0000",
			pointerEvents: "none",
		},
		cardBody: {
			padding: "14px",
			display: "flex",
			flexDirection: "column",
			gap: "12px",
		},
		nullRow: {
			display: "flex",
			alignItems: "center",
			gap: "8px",
			cursor: "pointer",
			fontSize: "13px",
			color: "#dc2626",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		input: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		select: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			backgroundColor: "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		textarea: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			resize: "vertical",
			minHeight: "90px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		bttBox: {
			border: "1px solid #e5e7eb",
			borderRadius: "6px",
			overflow: "hidden",
			maxHeight: "200px",
			overflowY: "auto",
		},
		bttRow: (checked, danger) => ({
			display: "flex",
			alignItems: "center",
			gap: "8px",
			padding: "7px 12px",
			borderBottom: "1px solid #f0f0f0",
			cursor: "pointer",
			fontSize: "13px",
			color: danger ? "#dc2626" : "#333",
			backgroundColor: checked ? (danger ? "#fee2e2" : "#f0f8ff") : "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		}),
		tagChip: {
			display: "inline-block",
			backgroundColor: "#1e2832",
			color: "white",
			borderRadius: "12px",
			padding: "2px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		hint: {
			margin: 0,
			fontSize: "11px",
			color: "#9ca3af",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		footerText: {
			marginRight: "auto",
			fontSize: "13px",
			color: "#6b7280",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		overlay: {
			position: "absolute",
			inset: 0,
			backgroundColor: "rgba(255,255,255,0.95)",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			alignItems: "center",
			zIndex: 5,
		},
	};

	// Renders a toggleable field card (uses a function, not a component, so text
	// inputs keep focus across re-renders).
	const renderField = (fieldKey, icon, label, accent, body) => {
		const active = fieldChanges[fieldKey];
		return (
			<div style={active ? s.cardActive : s.card}>
				<div
					style={s.cardHeader(active)}
					onClick={() => handleToggleChange(fieldKey, !active)}
				>
					<input type="checkbox" checked={active} readOnly style={s.checkbox} />
					{icon(active, accent)}
					<span style={s.cardLabel(active, accent)}>{label}</span>
				</div>
				{active && <div style={s.cardBody}>{body}</div>}
			</div>
		);
	};

	const formatItemDate = (dateValue) => {
		if (!dateValue) return "N/A";
		try {
			const date = new Date(dateValue);
			return !isNaN(date.getTime()) ? date.toLocaleDateString() : "N/A";
		} catch (error) {
			return "N/A";
		}
	};
	const formatBTTStatus = (bttStatus) => {
		if (!bttStatus) return "N/A";
		if (Array.isArray(bttStatus))
			return bttStatus.length > 0 ? bttStatus.join(", ") : "N/A";
		if (typeof bttStatus === "string") return bttStatus.trim() || "N/A";
		return "N/A";
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
			<CraftModalHeader
				header="Bulk Edit"
				content={`${selectedItems.length} item(s) selected — pick the fields to update.`}
			/>
			<CraftModalBody>
				<div style={s.shell}>
					{/* Progress overlay */}
					{processingProgress && (
						<div style={s.overlay}>
							<div
								style={{
									width: "50px",
									height: "50px",
									border: "4px solid #f3f3f3",
									borderTop: "4px solid #cc0000",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									marginBottom: "20px",
								}}
							/>
							<div
								style={{
									fontSize: "17px",
									color: "#1e2832",
									fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
									marginBottom: "8px",
								}}
							>
								Processing Bulk Updates…
							</div>
							<div style={{ fontSize: "13px", color: "#6b7280" }}>
								Updating {processingProgress.total} selected items
							</div>
							<div
								style={{
									width: "280px",
									height: "8px",
									backgroundColor: "#f3f4f6",
									borderRadius: "4px",
									margin: "16px 0",
									overflow: "hidden",
									border: "1px solid #e5e7eb",
								}}
							>
								<div
									style={{
										width: `${(processingProgress.current / processingProgress.total) * 100}%`,
										height: "100%",
										backgroundColor: "#cc0000",
										transition: "width 0.3s ease",
									}}
								/>
							</div>
							<div style={{ fontSize: "12px", color: "#9ca3af" }}>
								{Math.round(
									(processingProgress.current / processingProgress.total) * 100,
								)}
								% Complete
							</div>
							{processingProgress.currentOperation && (
								<div
									style={{
										marginTop: "12px",
										fontSize: "12px",
										color: "#6b7280",
										fontStyle: "italic",
									}}
								>
									{processingProgress.currentOperation}
								</div>
							)}
						</div>
					)}

					{selectedItems.length === 0 ? (
						<div style={s.emptyBox}>
							No items selected for bulk edit. Please select items from the
							table first.
						</div>
					) : (
						<div style={s.scrollArea}>
							{/* Selected Items Table */}
							<div style={s.sectionHead}>
								<span style={{ fontSize: "16px" }}>📋</span>
								<h3 style={s.sectionTitle}>
									Selected Items ({selectedItems.length})
								</h3>
							</div>
							<div style={s.tableWrap}>
								<table style={s.table}>
									<thead>
										<tr>
											<th style={s.th}>S.No</th>
											<th style={s.th}>Program</th>
											<th style={s.th}>Vehicle No.</th>
											<th style={s.th}>Part No.</th>
											<th style={s.th}>Description</th>
											<th style={s.th}>Supplier</th>
											<th style={s.th}>LTB Date</th>
											<th style={s.th}>Build Date</th>
											<th style={s.th}>Responsible</th>
											<th style={s.th}>Follow-up Date</th>
											<th style={s.th}>Status Toggle</th>
											<th style={s.th}>BTT Status</th>
											<th style={s.th}>Old Part No</th>
											<th style={s.th}>New Part No</th>
											<th style={s.th}>Tags</th>
										</tr>
									</thead>
									<tbody>
										{selectedItems.map((item, index) => (
											<tr key={index}>
												<td style={s.td}>{index + 1}</td>
												<td style={s.td}>{item.programm || "N/A"}</td>
												<td style={s.td}>{item.vehicle_no || "N/A"}</td>
												<td style={s.td}>{item.part_no || "N/A"}</td>
												<td style={s.td}>{item.part_description || "N/A"}</td>
												<td style={s.td}>{item.supplier || "N/A"}</td>
												<td style={s.td}>
													{formatItemDate(item.ltb || item.ltb_date)}
												</td>
												<td style={s.td}>{formatItemDate(item.build_date)}</td>
												<td style={s.td}>{item.part_responsible || "N/A"}</td>
												<td style={s.td}>
													{formatItemDate(item.part_followup_date)}
												</td>
												<td style={s.td}>
													{item.snr_ma_nahme_status === null
														? "None"
														: item.snr_ma_nahme_status === "in Arbeit"
															? "In Progress"
															: item.snr_ma_nahme_status === "erledigt"
																? "Completed"
																: item.snr_ma_nahme_status || "N/A"}
												</td>
												<td style={s.td}>
													{formatBTTStatus(item.status_btt || item.btt_status)}
												</td>
												<td style={s.td}>{item.old_snr || "N/A"}</td>
												<td style={s.td}>{item.new_snr || "N/A"}</td>
												<td style={s.td}>
													{item.tags
														? item.tags
																.split(",")
																.map((t) => t.trim())
																.filter(Boolean)
																.join(", ")
														: "N/A"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Bulk Edit Fields */}
							<div style={s.sectionHead}>
								<span style={{ fontSize: "16px" }}>⚙️</span>
								<h3 style={s.sectionTitle}>Select Fields to Update</h3>
							</div>
							<div style={s.grid}>
								{/* LTB Date */}
								{renderField(
									"ltb_date",
									(active, accent) => (
										<Calendar size={18} color={active ? accent : "#6b7280"} />
									),
									"LTB Date (from Dates column)",
									"#1e2832",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.ltb_date === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"ltb_date",
														e.target.checked ? "SET_TO_NULL" : null,
													)
												}
											/>
											Set to Empty/NULL (clear LTB date)
										</label>
										{bulkEditData.ltb_date !== "SET_TO_NULL" && (
											<input
												type="date"
												style={s.input}
												value={dateInputValue(bulkEditData.ltb_date)}
												onChange={(e) =>
													handleFieldChange(
														"ltb_date",
														dateFromInput(e.target.value),
													)
												}
											/>
										)}
									</>,
								)}

								{/* Build Date */}
								{renderField(
									"build_date",
									(active, accent) => (
										<Calendar size={18} color={active ? accent : "#6b7280"} />
									),
									"Assembly Date",
									"#1e2832",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.build_date === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"build_date",
														e.target.checked ? "SET_TO_NULL" : null,
													)
												}
											/>
											Set to Empty/NULL (clear Build date)
										</label>
										{bulkEditData.build_date !== "SET_TO_NULL" && (
											<input
												type="date"
												style={s.input}
												value={dateInputValue(bulkEditData.build_date)}
												onChange={(e) =>
													handleFieldChange(
														"build_date",
														dateFromInput(e.target.value),
													)
												}
											/>
										)}
									</>,
								)}

								{/* BTT Status */}
								{renderField(
									"btt_status",
									() => null,
									"BTT Status",
									"#cc0000",
									<div style={s.bttBox}>
										<label style={s.bttRow(bttIsEmptySentinel, true)}>
											<input
												type="checkbox"
												checked={bttIsEmptySentinel}
												onChange={() =>
													handleFieldChange(
														"btt_status",
														bttIsEmptySentinel ? [] : ["SET_TO_EMPTY"],
													)
												}
											/>
											Set to Empty/NULL
										</label>
										{STATUS_BTT_OPTIONS.map((option) => {
											const checked =
												!bttIsEmptySentinel && bttValues.includes(option);
											return (
												<label key={option} style={s.bttRow(checked, false)}>
													<input
														type="checkbox"
														checked={checked}
														onChange={() => toggleBttOption(option)}
													/>
													{option}
												</label>
											);
										})}
									</div>,
								)}

								{/* Responsible Person */}
								{renderField(
									"part_responsible",
									(active, accent) => (
										<Users size={18} color={active ? accent : "#6b7280"} />
									),
									"Responsible Person",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={
													bulkEditData.part_responsible === "SET_TO_NULL"
												}
												onChange={(e) =>
													handleFieldChange(
														"part_responsible",
														e.target.checked ? "SET_TO_NULL" : "",
													)
												}
											/>
											Set to Empty/NULL (clear responsible person)
										</label>
										{bulkEditData.part_responsible !== "SET_TO_NULL" && (
											<input
												type="text"
												style={s.input}
												placeholder="Enter responsible person name"
												value={
													bulkEditData.part_responsible === "SET_TO_NULL"
														? ""
														: bulkEditData.part_responsible
												}
												onChange={(e) => {
													const originalValue = e.target.value;
													const newValue = originalValue.replace(
														/[^a-zA-Z0-9\s.\-_'/]/g,
														"",
													);
													if (
														originalValue !== newValue &&
														originalValue.length > newValue.length
													) {
														alert(
															"Warning: Only letters, numbers, spaces, dots, hyphens, underscores, apostrophes, and forward slashes are allowed in responsible fields.",
														);
													}
													handleFieldChange("part_responsible", newValue);
												}}
											/>
										)}
									</>,
								)}

								{/* Follow-up Date */}
								{renderField(
									"part_followup_date",
									(active, accent) => (
										<Clock size={18} color={active ? accent : "#6b7280"} />
									),
									"Follow-up Date",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={
													bulkEditData.part_followup_date === "SET_TO_NULL"
												}
												onChange={(e) =>
													handleFieldChange(
														"part_followup_date",
														e.target.checked ? "SET_TO_NULL" : null,
													)
												}
											/>
											Set to Empty/NULL (clear follow-up date)
										</label>
										{bulkEditData.part_followup_date !== "SET_TO_NULL" && (
											<input
												type="date"
												style={s.input}
												value={dateInputValue(bulkEditData.part_followup_date)}
												onChange={(e) =>
													handleFieldChange(
														"part_followup_date",
														dateFromInput(e.target.value),
													)
												}
											/>
										)}
									</>,
								)}

								{/* Archive Status */}
								{renderField(
									"archive",
									(active, accent) => (
										<Archive size={18} color={active ? accent : "#6b7280"} />
									),
									"Archive Status",
									"#cc0000",
									<select
										style={s.select}
										value={bulkEditData.archive}
										onChange={(e) =>
											handleFieldChange("archive", e.target.value)
										}
									>
										{ARCHIVE_OPTIONS.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>,
								)}

								{/* Old Part No */}
								{renderField(
									"old_snr",
									(active, accent) => (
										<Hash size={18} color={active ? accent : "#6b7280"} />
									),
									"Old Part No",
									"#cc0000",
									<input
										type="text"
										style={s.input}
										placeholder="e.g. 81.41300-0004"
										value={bulkEditData.old_snr}
										onChange={(e) =>
											handleFieldChange("old_snr", e.target.value)
										}
									/>,
								)}

								{/* New Part No */}
								{renderField(
									"new_snr",
									(active, accent) => (
										<Hash size={18} color={active ? accent : "#6b7280"} />
									),
									"New Part No",
									"#cc0000",
									<input
										type="text"
										style={s.input}
										placeholder="e.g. 81.41300-0005"
										value={bulkEditData.new_snr}
										onChange={(e) =>
											handleFieldChange("new_snr", e.target.value)
										}
									/>,
								)}

								{/* Status Toggle */}
								{renderField(
									"snr_ma_nahme_status",
									(active, accent) => (
										<ToggleLeft size={18} color={active ? accent : "#6b7280"} />
									),
									"Status Toggle (None / in Arbeit / erledigt)",
									"#cc0000",
									<select
										style={s.select}
										value={
											bulkEditData.snr_ma_nahme_status === null
												? "null"
												: bulkEditData.snr_ma_nahme_status || "null"
										}
										onChange={(e) =>
											handleFieldChange(
												"snr_ma_nahme_status",
												e.target.value === "null" ? null : e.target.value,
											)
										}
									>
										<option value="null">None (Empty/Null)</option>
										<option value="in Arbeit">in Arbeit (In Progress)</option>
										<option value="erledigt">erledigt (Completed)</option>
									</select>,
								)}

								{/* Tags */}
								{renderField(
									"tags",
									(active, accent) => (
										<Tag size={18} color={active ? accent : "#6b7280"} />
									),
									"Tags",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.tags === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"tags",
														e.target.checked ? "SET_TO_NULL" : "",
													)
												}
											/>
											🗑️ Set to Empty/NULL (clear all tags)
										</label>
										{bulkEditData.tags !== "SET_TO_NULL" && (
											<>
												<input
													type="text"
													style={s.input}
													placeholder="e.g. urgent, review, hold"
													value={bulkEditData.tags || ""}
													onChange={(e) =>
														handleFieldChange("tags", e.target.value)
													}
												/>
												{(bulkEditData.tags || "").trim() && (
													<div
														style={{
															display: "flex",
															flexWrap: "wrap",
															gap: "4px",
														}}
													>
														{(bulkEditData.tags || "")
															.split(",")
															.map((t) => t.trim())
															.filter(Boolean)
															.map((t) => (
																<span key={t} style={s.tagChip}>
																	{t}
																</span>
															))}
													</div>
												)}
												<p style={s.hint}>
													These tags will replace existing tags on all selected
													items.
												</p>
											</>
										)}
									</>,
								)}

								{/* Comment (spans two columns) */}
								<div style={{ gridColumn: "1 / -1" }}>
									{renderField(
										"part_comment",
										(active, accent) => (
											<MessageSquare
												size={18}
												color={active ? accent : "#6b7280"}
											/>
										),
										"Add Comment (will be added to Parts Comments)",
										"#cc0000",
										<textarea
											style={s.textarea}
											placeholder="This will be added to the Parts Comments for all selected items."
											value={bulkEditData.part_comment}
											onChange={(e) =>
												handleFieldChange("part_comment", e.target.value)
											}
										/>,
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</CraftModalBody>
			<CraftModalFooter>
				<span style={s.footerText}>
					{hasChanges
						? `${Object.values(fieldChanges).filter(Boolean).length} field(s) will be updated`
						: "No changes selected"}
				</span>
				<CraftButton
					variant="plain"
					size="m"
					onClick={onClose}
					disabled={isSaving}
				>
					Cancel
				</CraftButton>
				<CraftButton
					variant="primary"
					size="m"
					onClick={handleSave}
					disabled={!hasChanges || selectedItems.length === 0 || isSaving}
				>
					{isSaving
						? "Applying Changes…"
						: `Apply to ${selectedItems.length} Items`}
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
};

export default VFFBulkEditModal;
